"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, CircleQuestionMark as HelpCircle, Clock, MapPin, ShieldCheck, Wrench, CalendarCheck, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqList = [{
  id: "item-1",
  question: "What areas of Jonesboro and Clayton County do you serve?",
  answer: "We provide full residential and commercial emergency plumbing across all of Jonesboro, GA, including Downtown Jonesboro, Tara Boulevard corridor, Lake Spivey area, Irondale, and neighboring communities throughout Clayton County and South Metro Atlanta.",
  icon: MapPin,
  category: "Coverage"
}, {
  id: "item-2",
  question: "Do you provide genuine 24/7 emergency plumbing service?",
  answer: "Yes. Our master plumbers are on standby 24 hours a day, 7 days a week, 365 days a year—including nights, weekends, and all major holidays. When a pipe bursts or a sewer backs up at 2 AM, a live local dispatcher takes your call.",
  icon: Clock,
  category: "Availability"
}, {
  id: "item-3",
  question: "How fast can your technicians arrive at my Jonesboro property?",
  answer: "Our rapid-response mobile units are stationed throughout Jonesboro and Clayton County, allowing us to arrive on-site within 30 to 60 minutes for urgent plumbing emergencies like burst pipes, active water leaks, and main drain clogs.",
  icon: ShieldCheck,
  category: "Response Time"
}, {
  id: "item-4",
  question: "What plumbing services do you offer?",
  answer: "We handle complete emergency and standard plumbing solutions: burst pipe repair, full repiping, sewer line camera inspection and repair, tank and tankless water heater diagnostics and replacement, drain jetting, toilet clogs, garbage disposal repair, faucet replacement, and non-invasive slab leak detection.",
  icon: Wrench,
  category: "Services"
}, {
  id: "item-5",
  question: "How do I request emergency service right now?",
  answer: "For immediate emergencies, call our direct 24/7 hotline. For scheduled maintenance, inspections, or non-urgent repairs, you can submit an online service request through our contact page.",
  icon: PhoneCall,
  category: "Booking"
}, {
  id: "item-6",
  question: "Are your plumbers licensed, bonded, and insured in Georgia?",
  answer: "Every technician on our crew is fully licensed in the State of Georgia, background-checked, insured, and thoroughly trained on the latest residential and commercial plumbing codes.",
  icon: CalendarCheck,
  category: "Assurance"
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const [activeItem, setActiveItem] = useState<string>("item-1");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="faq" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Decorative subtle background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-18">
          <Badge variant="outline" className="mb-4 border-accent/40 bg-accent/10 text-foreground font-semibold px-3.5 py-1 text-xs uppercase tracking-wider rounded-full inline-flex items-center gap-1.5">
            <HelpCircle className="size-3.5 text-accent" />
            <span>Got Questions? We Have Answers</span>
          </Badge>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-primary tracking-tight leading-tight mb-4">
            Jonesboro Emergency Plumbing FAQ
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg font-default leading-relaxed">
            Fast, clear answers about our 24/7 emergency coverage, arrival times, and plumbing repair services across Jonesboro and Clayton County.
          </p>
        </div>

        {/* Content Grid: FAQ Accordion + Quick Action Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Main Accordions */}
          <motion.div className="lg:col-span-8 space-y-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }}>
            <Accordion type="single" collapsible value={activeItem} onValueChange={setActiveItem} className="w-full space-y-4">
              {faqList.map((faq, i) => {
              const IconComponent = faq.icon;
              return <motion.div key={faq.id} variants={itemVariants} data-index={i} className="w-full">
                    <AccordionItem value={faq.id} className="border border-border/80 bg-card rounded-xl px-5 sm:px-6 transition-all duration-200 hover:border-primary/40 hover:shadow-sm data-[state=open]:border-primary/60 data-[state=open]:shadow-md data-[state=open]:bg-card">
                      <AccordionTrigger className="py-5 text-left hover:no-underline group [&>svg]:hidden">
                        <div className="flex items-center gap-3.5 sm:gap-4 flex-1 pr-4">
                          <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                            <IconComponent className="size-5" />
                          </div>
                          <div className="space-y-1">
                            <span className="text-xs font-semibold text-accent uppercase tracking-wide block">
                              {faq.category}
                            </span>
                            <span className="text-base sm:text-lg font-default font-semibold text-foreground group-hover:text-primary transition-colors duration-150">
                              {faq.question}
                            </span>
                          </div>
                        </div>

                        {/* Custom Gold Accent Expand Icon Indicator */}
                        <div className="size-8 rounded-full bg-accent/15 text-foreground flex items-center justify-center shrink-0 transition-transform duration-300 group-[&[data-state=open]]:rotate-180 group-[&[data-state=open]]:bg-accent group-[&[data-state=open]]:text-accent-foreground">
                          <ChevronRight className="size-4 rotate-90" />
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="pt-1 pb-5 text-muted-foreground font-default text-sm sm:text-base leading-relaxed pl-13 sm:pl-14 border-t border-border/40 mt-1">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>;
            })}
            </Accordion>
          </motion.div>

          {/* Sidebar CTA Card */}
          <div className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl bg-primary text-primary-foreground p-6 sm:p-8 shadow-xl border border-primary/20 relative overflow-hidden">
              {/* Background accent glow */}
              <div className="absolute -top-12 -right-12 size-36 rounded-full bg-accent/20 blur-2xl pointer-events-none" />

              <div className="relative z-10 space-y-5">
                <div className="inline-flex size-12 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-sm">
                  <PhoneCall className="size-6 animate-pulse" />
                </div>

                <div>
                  <h3 className="text-xl font-default font-bold text-primary-foreground mb-1">
                    Have an Urgent Leak or Backup?
                  </h3>
                  <p className="text-primary-foreground/85 text-sm leading-relaxed">
                    Our live dispatcher is ready 24/7. Technicians can be dispatched immediately to your address in Jonesboro.
                  </p>
                </div>

                <div className="pt-2 space-y-3">
                  <a href="tel:4708479076" className="block w-full" aria-label="Call emergency plumbing hotline now">
                    <Button variant="default" size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-md cursor-pointer transition-transform duration-150 active:scale-[0.98]">
                      <PhoneCall className="size-4 mr-1" />
                      <span>Call 24/7 Dispatch</span>
                    </Button>
                  </a>

                  <Link to="/contact/" className="block w-full">
                    <Button variant="outline" size="lg" className="w-full bg-primary/40 border-primary-foreground/20 text-primary-foreground hover:bg-primary/70 hover:text-primary-foreground cursor-pointer">
                      <span>Online Service Request</span>
                    </Button>
                  </Link>
                </div>

                <div className="pt-4 border-t border-primary-foreground/15 flex items-center justify-between text-xs text-primary-foreground/80">
                  <div className="flex items-center gap-1.5">
                    <span className="size-2 rounded-full bg-emerald-400" />
                    <span>Live Dispatch Active</span>
                  </div>
                  <span>Avg Response: 30-45m</span>
                </div>
              </div>
            </div>

            {/* Quick Service Links Card */}
            <div className="rounded-2xl bg-secondary text-secondary-foreground p-6 border border-border">
              <h4 className="text-base font-default font-bold text-foreground mb-3 flex items-center gap-2">
                <Wrench className="size-4 text-primary" />
                <span>Popular Emergency Repairs</span>
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/water-heater-repair/" className="flex items-center justify-between py-1.5 text-muted-foreground hover:text-primary transition-colors font-medium group">
                    <span>Water Heater Repair</span>
                    <ChevronRight className="size-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </Link>
                </li>
                <li>
                  <Link to="/pipe-repair/" className="flex items-center justify-between py-1.5 text-muted-foreground hover:text-primary transition-colors font-medium group">
                    <span>Burst Pipe Repair</span>
                    <ChevronRight className="size-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </Link>
                </li>
                <li>
                  <Link to="/drain-cleaning/" className="flex items-center justify-between py-1.5 text-muted-foreground hover:text-primary transition-colors font-medium group">
                    <span>Emergency Drain Cleaning</span>
                    <ChevronRight className="size-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </Link>
                </li>
                <li>
                  <Link to="/leak-detection-repair/" className="flex items-center justify-between py-1.5 text-muted-foreground hover:text-primary transition-colors font-medium group">
                    <span>Slab & Water Leak Detection</span>
                    <ChevronRight className="size-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}